import { CategoryCard } from "./CategoryCard";
import { useShopifyQuery } from "@/hooks/useShopifyQuery";
import { useMemo } from "react";
import hcdBloomfangHero from "@/assets/hcd-bloomfang-hero.webp";

export function CategoryShowcaseGrid() {
  const { collections, products } = useShopifyQuery({ enableFullCatalog: true });

  // Calculate product counts for each category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    // Helper to count products for a single collection
    const getProductCount = (handle: string) => {
      const collection = collections.find(c => c.handle === handle);
      if (!collection || !collection.productHandles) return 0;
      return products.filter(p => collection.productHandles!.includes(p.handle)).length;
    };
    
    // Helper to count products across multiple collections (for aggregated categories)
    const getAggregatedCount = (handles: string[]) => {
      const uniqueHandles = new Set<string>();
      handles.forEach(handle => {
        const collection = collections.find(c => c.handle === handle);
        if (collection && collection.productHandles) {
          collection.productHandles.forEach(h => uniqueHandles.add(h));
        }
      });
      return products.filter(p => uniqueHandles.has(p.handle)).length;
    };

    counts.goddess = getProductCount('goddess');
    counts.jewelry = getProductCount('jewelry');
    counts.accessories = getProductCount('accessories');
    counts.lifestyle = getProductCount('lifestyle-decor');
    counts.bathBody = getProductCount('bath-body');
    counts.chromeHearts = getProductCount('chrome-hearts');
    // Hot Chocolate Design - designer shoes
    counts.hotChocolateDesign = products.filter(p => 
      p.tags?.some(tag => tag.toLowerCase() === 'hot chocolate design')
    ).length;
    // Apparel aggregates multiple collections
    counts.apparel = getAggregatedCount(['tops', 'bottoms', 'dresses', 'one-pieces', 'lounge-wear']);

    return counts;
  }, [collections, products]);

  const categories = [
    {
      image: "/lovable-uploads/jewelry-v2.jpg",
      title: "Jewelry",
      tagline: "Handcrafted pieces to adorn your spirit",
      productCount: categoryCounts.jewelry,
      badge: "💎 Handcrafted",
      link: "/collections/jewelry",
    },
    {
      image: "/lovable-uploads/goddess-collection-v5.jpg",
      title: "Goddess",
      tagline: "Embrace your divine feminine energy",
      productCount: categoryCounts.goddess,
      badge: "✨ Featured",
      link: "/collections/goddess",
      featured: true,
    },
    {
      image: hcdBloomfangHero,
      title: "Hot Chocolate Design",
      tagline: "Artisan Venezuelan designer shoes",
      productCount: categoryCounts.hotChocolateDesign,
      badge: "🎨 Designer",
      link: "/collections/hot-chocolate-designs",
      featured: true,
    },
    {
      image: "/lovable-uploads/apparel-v3.jpg",
      title: "Apparel",
      tagline: "Flow through life in comfort & style",
      productCount: categoryCounts.apparel,
      link: "/collections/apparel",
    },
    {
      image: "/lovable-uploads/lifestyle-v2.jpg",
      title: "Lifestyle & Decor",
      tagline: "Create your sacred space",
      productCount: categoryCounts.lifestyle,
      link: "/collections/lifestyle-decor",
    },
    {
      image: "/lovable-uploads/chrome-hearts-v5.jpg",
      title: "Chrome Hearts",
      tagline: "Urban edge meets bohemian soul",
      productCount: categoryCounts.chromeHearts,
      badge: "🖤 Special",
      link: "/collections/chrome-hearts",
      featured: true,
    },
    {
      image: "/lovable-uploads/bath-body-v2.jpg",
      title: "Bath & Body",
      tagline: "Self-care essentials for your ritual",
      productCount: categoryCounts.bathBody,
      link: "/collections/bath-body",
    },
    {
      image: "/lovable-uploads/accessories-showcase.jpg",
      title: "Accessories",
      tagline: "Bags, shoes, and statement pieces",
      productCount: categoryCounts.accessories,
      link: "/collections/accessories",
    },
  ];

  return (
    <section className="pb-8">
      <div className="container-wide px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <CategoryCard
              key={category.link}
              {...category}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
